import cx from 'clsx';
import { addDays, differenceInCalendarDays, endOfMonth, format, parseISO, startOfMonth } from 'date-fns';
import { PeriodWidget } from 'components/PeriodWidget';
import { type CSSProperties, type ReactElement, type ReactNode } from 'react';
import type { PlanMeta } from '../state';
import * as styles from './ScheduleTimeline.css';
import type { ScheduleAnalysis } from './scheduleAnalysis';

type TimelineSegment = {
  from: string;
  to: string;
  tone: keyof typeof styles.barTone;
};

function periodStyle(from: string, to: string, rangeStart: Date, totalDays: number): CSSProperties {
  const left = Math.max(0, Math.min(totalDays, differenceInCalendarDays(parseISO(from), rangeStart)));
  const right = Math.max(0, Math.min(totalDays, differenceInCalendarDays(addDays(parseISO(to), 1), rangeStart)));

  return {
    left: `${(left / totalDays) * 100}%`,
    width: `${((Math.max(right, left) - left) / totalDays) * 100}%`,
  };
}

function TimelineRow({
  label,
  labelFrom,
  labelTone,
  segments,
  rangeStart,
  totalDays,
}: {
  label: ReactNode;
  labelFrom: string;
  labelTone: keyof typeof styles.labelTone;
  segments: TimelineSegment[];
  rangeStart: Date;
  totalDays: number;
}): ReactElement {
  const labelLeft = Math.max(
    0,
    Math.min(100, (differenceInCalendarDays(parseISO(labelFrom), rangeStart) / totalDays) * 100),
  );

  return (
    <div className={styles.row}>
      <span
        className={cx(styles.label, styles.labelTone[labelTone])}
        style={{ left: `${labelLeft}%`, maxWidth: `${100 - labelLeft}%` }}
      >
        {label}
      </span>
      <div className={styles.track}>
        {segments.map((segment) => (
          <span
            key={`${segment.tone}-${segment.from}-${segment.to}`}
            className={styles.barTone[segment.tone]}
            style={periodStyle(segment.from, segment.to, rangeStart, totalDays)}
          />
        ))}
      </div>
    </div>
  );
}

export function ScheduleTimeline({
  analysis,
  destination,
}: {
  analysis: ScheduleAnalysis;
  destination: PlanMeta;
}): ReactElement {
  const dates = [
    destination.date_from,
    destination.date_to,
    analysis.first,
    analysis.last,
    analysis.alignedFirst,
    analysis.alignedLast,
  ]
    .filter((value): value is string => value !== null)
    .map((value) => parseISO(value));
  const firstDate = new Date(Math.min(...dates.map((date) => date.getTime())));
  const lastDate = new Date(Math.max(...dates.map((date) => date.getTime())));
  const rangeStart = startOfMonth(firstDate);
  const rangeEnd = endOfMonth(lastDate);
  const totalDays = differenceInCalendarDays(addDays(rangeEnd, 1), rangeStart);

  const sourceSegments: TimelineSegment[] =
    analysis.first && analysis.last ? [{ from: analysis.first, to: analysis.last, tone: 'source' }] : [];
  const alignedSegments: TimelineSegment[] = [];

  if (analysis.alignedFirst && analysis.alignedLast) {
    const fittedLast = analysis.alignedLast < destination.date_to ? analysis.alignedLast : destination.date_to;
    if (analysis.alignedFirst <= fittedLast) {
      alignedSegments.push({ from: analysis.alignedFirst, to: fittedLast, tone: 'aligned' });
    }
    if (analysis.alignedLast > destination.date_to) {
      const firstOverflowDate = format(addDays(parseISO(destination.date_to), 1), 'yyyy-MM-dd');
      alignedSegments.push({
        from: analysis.alignedFirst > firstOverflowDate ? analysis.alignedFirst : firstOverflowDate,
        to: analysis.alignedLast,
        tone: 'overflow',
      });
    }
  }

  const sourceLabel =
    analysis.first && analysis.last ? (
      <>
        Schedule <PeriodWidget date_from={analysis.first} date_to={analysis.last} />
      </>
    ) : (
      'No source schedule'
    );

  return (
    <div className={styles.timeline} aria-label="Schedule period comparison">
      <div className={styles.months}>
        <span>{format(rangeStart, 'MMM yyyy')}</span>
        {format(rangeStart, 'yyyy-MM') !== format(rangeEnd, 'yyyy-MM') && <span>{format(rangeEnd, 'MMM yyyy')}</span>}
      </div>
      <div className={styles.rows}>
        <TimelineRow
          label={sourceLabel}
          labelFrom={analysis.first ?? destination.date_from}
          labelTone="source"
          segments={sourceSegments}
          rangeStart={rangeStart}
          totalDays={totalDays}
        />
        <TimelineRow
          label={
            <>
              Destination <PeriodWidget date_from={destination.date_from} date_to={destination.date_to} />
            </>
          }
          labelFrom={destination.date_from}
          labelTone="destination"
          segments={[{ from: destination.date_from, to: destination.date_to, tone: 'destination' }]}
          rangeStart={rangeStart}
          totalDays={totalDays}
        />
        {analysis.alignedFirst && analysis.alignedLast && (
          <TimelineRow
            label={
              <>
                Aligned <PeriodWidget date_from={analysis.alignedFirst} date_to={analysis.alignedLast} />
              </>
            }
            labelFrom={analysis.alignedFirst}
            labelTone="aligned"
            segments={alignedSegments}
            rangeStart={rangeStart}
            totalDays={totalDays}
          />
        )}
      </div>
    </div>
  );
}
